import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ShieldAlert,
  ShieldCheck,
  Lock,
  AlertOctagon,
  Skull,
  Crosshair,
  Code2,
  Image as ImageIcon,
  Wrench,
  Scale,
  CheckCircle2,
  Loader2,
  ArrowRight,
  ChevronRight,
  Gavel,
  BadgeCheck,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const publicClient = axios.create({ baseURL: API });

function SectionLabel({ children, dataTestId }) {
  return (
    <div
      className="inline-flex items-center gap-2 text-[10px] sm:text-[11px] font-bold tracking-[0.28em] text-zinc-500 uppercase"
      data-testid={dataTestId}
    >
      <span className="w-6 h-px bg-zinc-400" />
      {children}
    </div>
  );
}

function HeroScanWidget() {
  const [url, setUrl] = useState("");
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const resultRef = useRef(null);

  const runScan = async (e) => {
    e?.preventDefault();
    setError("");
    setResult(null);
    if (!url.trim() || url.length < 4) {
      setError("Enter a website URL to scan.");
      return;
    }
    setScanning(true);
    try {
      const r = await publicClient.post("/public/audit-teaser", { url: url.trim() });
      setResult(r.data);
      // Scroll the result into view on mobile (it appears below the input).
      setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 100);
    } catch (err) {
      setError(err?.response?.data?.detail || "Scan failed. Try a different URL or schedule a manual review.");
    } finally {
      setScanning(false);
    }
  };

  const scheduleHref = result
    ? `/schedule?site=${encodeURIComponent(result.target_url)}`
    : "/schedule";

  return (
    <div className="relative">
      <form
        onSubmit={runScan}
        className="bg-white border-2 border-zinc-950 p-5 sm:p-6 shadow-[8px_8px_0_0_rgba(9,9,11,1)]"
        data-testid="hero-scan-widget"
      >
        <label
          htmlFor="hero-url"
          className="block text-[10px] font-bold tracking-[0.28em] text-zinc-500 uppercase mb-2"
        >
          Free Compliance Scan · No signup
        </label>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-sm text-zinc-400 select-none">
              https://
            </span>
            <input
              id="hero-url"
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="yourbusiness.com"
              disabled={scanning}
              autoComplete="url"
              spellCheck={false}
              className="w-full h-12 pl-[68px] pr-3 border-2 border-zinc-300 focus:border-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-700 focus:ring-offset-1 font-mono text-sm rounded-none disabled:bg-zinc-50"
              data-testid="hero-url-input"
            />
          </div>
          <button
            type="submit"
            disabled={scanning}
            className="h-12 px-6 bg-blue-700 hover:bg-blue-800 active:bg-blue-900 text-white font-bold uppercase tracking-wider text-sm whitespace-nowrap transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            data-testid="hero-scan-submit-button"
          >
            {scanning ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Scanning…
              </>
            ) : (
              <>
                Run Free Scan
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
        <p className="mt-2 text-[11px] text-zinc-500 font-mono leading-snug">
          Analyzing DOM hierarchy, ARIA states, and asset metadata. ~20 seconds.
        </p>
        {error && (
          <div
            role="alert"
            className="mt-3 flex items-start gap-2 bg-red-50 border-2 border-red-300 p-3 text-sm text-red-800"
            data-testid="hero-scan-error"
          >
            <AlertOctagon className="w-4 h-4 mt-0.5 shrink-0" />
            {error}
          </div>
        )}
      </form>

      {scanning && (
        <div
          className="mt-4 bg-zinc-950 text-zinc-100 p-5 border-2 border-zinc-950"
          aria-live="polite"
          data-testid="hero-scan-loading"
        >
          <div className="text-[10px] font-bold tracking-[0.28em] text-blue-400 uppercase mb-3">
            Live scan in progress
          </div>
          <div className="font-mono text-xs leading-relaxed space-y-1">
            <div><span className="text-blue-400">▶</span> Fetching DOM tree…</div>
            <div><span className="text-blue-400">▶</span> Parsing heading hierarchy…</div>
            <div><span className="text-blue-400">▶</span> Crawling images for alt-text gaps…</div>
            <div><span className="text-blue-400">▶</span> Auditing form labels & ARIA states…</div>
            <div className="text-zinc-500">  Compiling redacted summary…</div>
          </div>
        </div>
      )}

      {result && !scanning && (
        <div
          ref={resultRef}
          className="mt-4 bg-zinc-950 text-white border-2 border-zinc-950 shadow-[8px_8px_0_0_rgba(220,38,38,1)]"
          data-testid="hero-scan-results-card"
        >
          <div className="px-5 sm:px-6 pt-5 pb-3 border-b border-zinc-800 flex items-start gap-3">
            <AlertOctagon className="w-6 h-6 text-red-500 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] font-bold tracking-[0.28em] text-red-400 uppercase">
                Exposure Surface Detected
              </div>
              <div className="font-mono text-xs text-zinc-400 truncate" title={result.target_url}>
                {result.target_url}
              </div>
            </div>
          </div>
          <div className="px-5 sm:px-6 py-5 grid grid-cols-2 gap-4 sm:gap-6">
            <div>
              <div className="text-[10px] font-bold tracking-[0.28em] text-zinc-400 uppercase">
                Accessibility Findings
              </div>
              <div className="font-display text-5xl sm:text-6xl font-black text-white tabular-nums leading-none mt-1" data-testid="hero-scan-total-issues">
                {result.total_issues}
              </div>
              <div className="text-[10px] text-zinc-500 mt-1 leading-snug">
                WCAG failures + best-practice + manual-review
              </div>
            </div>
            <div>
              <div className="text-[10px] font-bold tracking-[0.28em] text-red-400 uppercase">
                WCAG Failures
              </div>
              <div className="font-display text-5xl sm:text-6xl font-black text-red-500 tabular-nums leading-none mt-1" data-testid="hero-scan-high-severity">
                {result.high_severity_count}
              </div>
              <div className="text-[10px] text-red-400/70 mt-1 leading-snug">
                high-severity · WCAG 2 A/AA
              </div>
            </div>
          </div>
          <div className="px-5 sm:px-6 pb-4">
            <div className="text-[10px] font-bold tracking-[0.28em] text-zinc-400 uppercase mb-2">
              Scanned with axe-core <span className="text-zinc-600">(same engine the attorneys cite)</span>
            </div>
            <p className="mt-1 text-[11px] text-zinc-500 leading-snug">
              Concrete rule names, locators, code references and severity-mapped
              remediation are reserved for the briefing call.
            </p>
          </div>
          <Link
            to={scheduleHref}
            className="block bg-blue-700 hover:bg-blue-800 active:bg-blue-900 text-white text-center font-bold uppercase tracking-wider text-sm py-4 transition-colors"
            data-testid="hero-scan-schedule-cta"
          >
            See Full Report — Schedule 10 Minute Call <ArrowRight className="w-4 h-4 inline ml-1 -mt-0.5" />
          </Link>
        </div>
      )}
    </div>
  );
}

function FooterUrlCapture() {
  const [url, setUrl] = useState("");

  const submit = (e) => {
    e.preventDefault();
    const cleaned = url.trim();
    const target = cleaned ? `/schedule?site=${encodeURIComponent(cleaned)}` : "/schedule";
    window.location.assign(target);
  };

  return (
    <form
      onSubmit={submit}
      className="bg-zinc-950 text-white border-2 border-blue-700 shadow-[10px_10px_0_0_rgba(29,78,216,1)]"
      data-testid="footer-url-capture"
    >
      <div className="px-6 sm:px-10 pt-8 pb-6">
        <div className="font-mono text-[11px] text-blue-400 mb-2">$ secure --my-site</div>
        <h3 className="font-display text-2xl sm:text-3xl font-black tracking-tight leading-tight">
          Do not wait for a demand letter.
        </h3>
        <p className="mt-2 text-zinc-300 text-sm sm:text-base leading-relaxed max-w-2xl">
          Check what automated legal bots see when they scan your site right now.
          One field. No spam. Direct routing to a confidential 10-minute briefing.
        </p>
      </div>
      <div className="px-6 sm:px-10 pb-8">
        <label htmlFor="footer-url" className="sr-only">Website URL</label>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            id="footer-url"
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://yourbusiness.com"
            spellCheck={false}
            autoComplete="url"
            className="flex-1 h-12 px-3 bg-zinc-900 border-2 border-zinc-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 focus:ring-offset-zinc-950 font-mono text-sm text-white placeholder:text-zinc-500 rounded-none"
            data-testid="footer-url-input"
          />
          <button
            type="submit"
            className="h-12 px-6 bg-blue-700 hover:bg-blue-600 text-white font-bold uppercase tracking-wider text-sm whitespace-nowrap transition-colors inline-flex items-center justify-center gap-2"
            data-testid="footer-secure-my-site-button"
          >
            Secure My Site
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </form>
  );
}

const PILLARS = [
  {
    n: "01",
    icon: Wrench,
    title: "In-Flight Remediation",
    desc: "Rebuilds broken heading hierarchies and stamps missing form labels directly into the active DOM the millisecond a page loads. The fix exists before a scanner can see the gap.",
  },
  {
    n: "02",
    icon: ImageIcon,
    title: "Dynamic Image Patching",
    desc: "Auto-injects approved alt-text across every image on every page. Litigation crawlers searching for unlabeled assets find nothing to weaponize.",
  },
  {
    n: "03",
    icon: ShieldAlert,
    title: "Zero-Friction Security Net",
    desc: "Hands your existing dev team a precise roadmap for manual assets (PDFs, embedded video) while the site stays legally shielded today — no rebuild required.",
  },
];

const TIERS = [
  { name: "Monthly", price: "40", period: "/month", testid: "pricing-tier-monthly", packageId: "monthly" },
  { name: "Quarterly", price: "100", period: "/quarter", note: "$33/mo · save 17%", testid: "pricing-tier-quarterly", packageId: "quarterly", highlight: true },
  { name: "Annual", price: "300", period: "/year", note: "$25/mo · save 38%", testid: "pricing-tier-annual", packageId: "annual" },
];

export default function MarketingLanding() {
  const [selectedTier, setSelectedTier] = useState("quarterly");
  const [checkoutBusy, setCheckoutBusy] = useState(false);

  useEffect(() => {
    document.title = "Silent Shield — ADA Compliance Engine Behind Your Website";
  }, []);

  const startCheckout = async () => {
    setCheckoutBusy(true);
    try {
      const r = await publicClient.post("/payments/v1/checkout/session", {
        package_id: selectedTier,
        origin_url: window.location.origin,
      });
      if (r.data?.url) {
        window.location.assign(r.data.url);
      }
    } catch (err) {
      // Fall back to /schedule if Stripe is mis-configured (e.g. no key)
      window.location.assign("/schedule");
    } finally {
      setCheckoutBusy(false);
    }
  };

  return (
    <div className="marketing-scope min-h-screen text-ink" data-testid="marketing-landing">
      {/* Skip-to-main for keyboard / screen-reader users (WCAG 2.4.1) */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-navy focus:text-parchment focus:px-4 focus:py-2 focus:font-bold focus:tracking-wider focus:uppercase focus:text-sm focus:ring-2 focus:ring-brass focus:ring-offset-2 focus:rounded-none"
      >
        Skip to main content
      </a>
      {/* TOP BAR — legal-editorial masthead */}
      <header className="border-b border-brass/40 bg-parchment/95 backdrop-blur sticky top-0 z-30">
        <div className="rule-brass-thick" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Silent Shield seal — brass-bordered emblem */}
            <div className="relative w-10 h-10 border-2 border-brass bg-navy grid place-items-center" data-testid="masthead-seal">
              <ShieldAlert className="w-4 h-4 text-brass-400" strokeWidth={2.5} />
              <div className="absolute -inset-1 border border-brass/50 pointer-events-none" aria-hidden="true" />
            </div>
            <div>
              <div className="font-legal font-black tracking-tight text-lg leading-none text-navy">
                Silent Shield
              </div>
              <div className="text-[9px] font-bold tracking-[0.28em] text-brass-600 uppercase mt-0.5">
                ⚜ ADA Interceptor · Est. 2026
              </div>
            </div>
          </div>
          <a
            href="#scan"
            className="hidden sm:inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.18em] text-navy hover:text-oxblood transition-colors"
          >
            Run Free Scan <ChevronRight className="w-3 h-3" />
          </a>
        </div>
      </header>

      <main id="main">


      {/* ====================== HERO ====================== */}
      <section
        id="scan"
        className="relative border-b border-brass/30 overflow-hidden"
        data-testid="hero-section"
      >
        {/* Parchment ambient — subtle warm gradient wash + faint grid, no
            saturated blobs. The palette carries the mood, not visual noise. */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute inset-0 bg-gradient-to-br from-parchment-50 via-parchment to-parchment-200/60" />
          <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-navy/8 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-oxblood/8 blur-3xl" />
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage:
                "linear-gradient(#0B1F3F 1px, transparent 1px), linear-gradient(90deg, #0B1F3F 1px, transparent 1px)",
              backgroundSize: "56px 56px",
            }}
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-28 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-7">
            <div className="flex items-center gap-3 text-brass-600" data-testid="hero-eyebrow">
              <div className="w-10 rule-brass" />
              <span className="text-[10px] font-bold tracking-[0.32em] uppercase font-mono">
                § I · Pattern Interrupter
              </span>
            </div>
            <h1
              className="font-legal text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.02] mt-6 text-navy"
              data-testid="hero-headline"
            >
              Don&rsquo;t be the next{" "}
              <span className="relative inline-block">
                <em className="font-italic-legal not-italic bg-gradient-to-br from-oxblood-700 to-oxblood bg-clip-text text-transparent italic">
                  lawsuit victim
                </em>
                <span className="absolute inset-x-0 -bottom-0.5 h-0.5 bg-brass" aria-hidden="true" />
              </span>{" "}
              over a bad website.
              <br className="hidden sm:block" />
              <span className="font-italic-legal italic text-ink-800 font-normal">
                Legal scanners are looking for you.
              </span>
            </h1>
            <p
              className="mt-8 text-base sm:text-lg text-ink-800 leading-relaxed max-w-2xl"
              data-testid="hero-subheadline"
            >
              Automated legal bots don&rsquo;t care about your website&rsquo;s design &mdash; they
              look for missing image labels and broken layouts to build high-volume lawsuits.
              Silent Shield runs an invisible, mid-air defense net inside the browser to
              neutralize <strong className="text-navy">95% of your legal exposure</strong>{" "}
              instantly &mdash; for less than the cost of a daily coffee.
            </p>

            {/* WCAG / Title III standards strip — brass-bordered legal badges. */}
            <div className="mt-8 flex flex-wrap gap-2" data-testid="hero-standards-strip">
              {[
                { label: "WCAG 2.1", glyph: "§" },
                { label: "WCAG 2.2", glyph: "§" },
                { label: "Level A", glyph: "⚖" },
                { label: "Level AA", glyph: "⚖" },
                { label: "Level AAA", glyph: "⚖" },
                { label: "ADA Title III", glyph: "§" },
              ].map(({ label, glyph }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-2 text-[10px] sm:text-[11px] font-bold tracking-[0.22em] uppercase px-3 py-1.5 border border-brass bg-parchment-50 text-navy shadow-[2px_2px_0_0_rgba(184,137,79,0.4)]"
                  data-testid={`standards-badge-${label.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <span className="text-oxblood font-legal text-sm leading-none">{glyph}</span>
                  {label}
                </span>
              ))}
            </div>

            <div className="mt-10 max-w-2xl">
              <HeroScanWidget />
            </div>
          </div>
          <div className="hidden lg:flex col-span-12 lg:col-span-5 items-center justify-end">
            <div className="bg-navy text-parchment p-8 w-full max-w-md border-2 border-navy shadow-[10px_10px_0_0_#B8894F] relative">
              {/* Corner brass filigree */}
              <div className="absolute top-2 left-2 w-3 h-3 border-t border-l border-brass/60" aria-hidden="true" />
              <div className="absolute top-2 right-2 w-3 h-3 border-t border-r border-brass/60" aria-hidden="true" />
              <div className="absolute bottom-2 left-2 w-3 h-3 border-b border-l border-brass/60" aria-hidden="true" />
              <div className="absolute bottom-2 right-2 w-3 h-3 border-b border-r border-brass/60" aria-hidden="true" />
              <div className="flex items-center gap-2 text-[10px] font-bold tracking-[0.32em] text-brass-400 uppercase mb-4">
                <span className="font-legal text-sm leading-none">§</span>
                What the scanners see
              </div>
              <pre className="font-mono text-[12px] leading-[1.7] text-parchment-50/95 whitespace-pre overflow-hidden">
{`<img src="hero.jpg">
<button>
  <svg></svg>
</button>
<h3>Welcome</h3>
<input type="email">
<a href="#">Read more</a>`}
              </pre>
              <div className="mt-5 pt-4 border-t border-brass/30 text-xs text-parchment/80 leading-relaxed">
                Every line above is a separately actionable{" "}
                <span className="text-brass-400 font-bold">WCAG 2.1 / 2.2</span> violation.
                <span className="block mt-2 text-brass-400 font-bold font-italic-legal italic">
                  Your overlay widget is not visible in this view.
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====================== SECTION 2 — ILLUSION OF SAFETY ====================== */}
      <section
        className="border-b border-brass/30 bg-parchment-200/40"
        data-testid="illusion-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-5">
            <div className="flex items-center gap-3 text-brass-600" data-testid="illusion-eyebrow">
              <div className="w-10 rule-brass" />
              <span className="text-[10px] font-bold tracking-[0.32em] uppercase font-mono">
                § II · Shattering the Overlay Myth
              </span>
            </div>
            <h2 className="font-legal text-4xl sm:text-5xl font-black tracking-tight leading-[1.05] mt-6 text-navy">
              Predatory crawlers{" "}
              <span className="font-italic-legal italic text-oxblood">don&rsquo;t see</span>{" "}
              your toolbar.
            </h2>
            <div className="mt-5 rule-brass max-w-[70%]" />
            <p className="mt-6 text-base sm:text-lg text-ink-800 leading-relaxed">
              They scan your raw source code. If your HTML is broken, your liability
              exposure remains one hundred percent active &mdash; toolbar visible or not.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-7 grid gap-4">
            {[
              {
                k: "I",
                title: "They run headless browsers — not Chrome",
                body: "Automated law-firm tools scrape your raw DOM tree before any client-side script executes. They never see the widget's render layer.",
              },
              {
                k: "II",
                title: "Overlays load AFTER the violation is recorded",
                body: "By the time the visual toolbar paints, the scanner has already snapshotted your unlabeled inputs, broken heading order, and missing alt attributes.",
              },
              {
                k: "III",
                title: "The widget fixes the toolbar — not the source",
                body: "A false sense of security while predatory bots compile your demand letter. The underlying HTML never changes. The exposure never closes.",
              },
            ].map((row) => (
              <div
                key={row.k}
                className="bg-parchment border-2 border-navy p-6 sm:p-7 grid grid-cols-[auto_1fr] gap-5 sm:gap-6 items-start relative"
                data-testid={`illusion-row-${row.k}`}
              >
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-brass via-brass-400 to-brass" aria-hidden="true" />
                <div className="font-legal italic text-oxblood/60 text-4xl sm:text-5xl font-black leading-none pt-1">
                  {row.k}
                </div>
                <div>
                  <div className="font-legal text-lg sm:text-xl font-bold tracking-tight text-navy">
                    {row.title}
                  </div>
                  <p className="mt-2 text-sm text-ink-800 leading-relaxed">{row.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====================== SECTION 2.5 — STANDARDS COVERAGE ====================== */}
      <section
        className="relative border-b border-brass/30 overflow-hidden"
        data-testid="standards-section"
      >
        <div className="absolute inset-0 pointer-events-none bg-parchment-50" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-5">
            <div className="flex items-center gap-3 text-brass-600" data-testid="standards-eyebrow">
              <div className="w-10 rule-brass" />
              <span className="text-[10px] font-bold tracking-[0.32em] uppercase font-mono">
                § II.V · Coverage Matrix
              </span>
            </div>
            <h2 className="font-legal text-4xl sm:text-5xl font-black tracking-tight leading-[1.05] mt-6 text-navy">
              Every standard the scanners run against.
              <br />
              <span className="font-italic-legal italic text-oxblood font-bold">
                Continuously.
              </span>
            </h2>
            <div className="mt-5 rule-brass max-w-[80%]" />
            <p className="mt-6 text-base sm:text-lg text-ink-800 leading-relaxed">
              Silent Shield validates your live DOM against every criterion in
              WCAG 2.1 <em className="font-italic-legal italic text-oxblood">and</em> the
              newly-adopted WCAG 2.2, across Levels A, AA, and AAA &mdash; plus the
              ADA Title III conformance path the DOJ referenced in its April 2024
              rulemaking.
            </p>
            <div className="mt-8 relative border-2 border-navy overflow-hidden shadow-[8px_8px_0_0_#B8894F]">
              <img
                src="https://images.unsplash.com/photo-1589391886645-d51941baf7fb?w=900&auto=format&fit=crop&q=70"
                alt="Judge's gavel and legal reference books stacked on a wooden desk"
                loading="lazy"
                decoding="async"
                className="w-full h-56 object-cover"
                data-testid="standards-image-legal"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/25 to-transparent" />
              <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between text-parchment">
                <div className="font-mono text-[10px] tracking-widest uppercase text-brass-400">
                  ⚜ Title III · § 12181 et seq.
                </div>
                <Gavel className="w-5 h-5 text-brass-400" strokeWidth={2} />
              </div>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                {
                  eyebrow: "WCAG 2.1",
                  title: "The active industry benchmark",
                  desc: "50 success criteria across perceivable, operable, understandable, and robust content — the exact ruleset every automated crawler runs first.",
                  levels: ["A · 25 rules", "AA · 13 rules", "AAA · 12 rules"],
                  test: "std-card-wcag21",
                },
                {
                  eyebrow: "WCAG 2.2 · Oct 2023",
                  title: "The newest court-cited target",
                  desc: "Nine new success criteria including Focus Not Obscured, Dragging Movements, and Target Size — the vulnerabilities the newest plaintiff-firm scripts prioritize.",
                  levels: ["+2 Level A", "+4 Level AA", "+3 Level AAA"],
                  test: "std-card-wcag22",
                },
                {
                  eyebrow: "Level A · AA · AAA",
                  title: "Full conformance ladder",
                  desc: "Level A blocks the lawsuit vector. Level AA is the DOJ-referenced conformance target. Level AAA closes the enhanced-accessibility gap for regulated industries.",
                  levels: ["A: minimum", "AA: recommended", "AAA: elevated"],
                  test: "std-card-levels",
                },
                {
                  eyebrow: "ADA Title III",
                  title: "Federal statutory exposure",
                  desc: "42 U.S.C. § 12181 covers public-accommodation websites. Federal courts have repeatedly held that WCAG 2.1 AA is the operative technical proxy for Title III conformance.",
                  levels: ["§ 12181–12189", "Federal jurisdiction", "State piggy-backs"],
                  test: "std-card-title-iii",
                },
              ].map((c) => (
                <div
                  key={c.test}
                  className="relative bg-parchment border-2 border-navy p-6 sm:p-7 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[6px_6px_0_0_#B8894F] group"
                  data-testid={c.test}
                >
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-brass via-brass-400 to-brass" aria-hidden="true" />
                  <div className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.28em] uppercase text-brass-600">
                    <span className="font-legal text-oxblood text-base leading-none">§</span> {c.eyebrow}
                  </div>
                  <div className="font-legal text-2xl font-black tracking-tight mt-3 text-navy leading-[1.15]">
                    {c.title}
                  </div>
                  <p className="mt-3 text-sm text-ink-800 leading-relaxed">{c.desc}</p>
                  <div className="mt-5 pt-3 border-t border-brass/30 flex flex-wrap gap-x-3 gap-y-1 font-mono text-[10px] text-navy uppercase tracking-widest">
                    {c.levels.map((l) => <span key={l}>{l}</span>)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ====================== SECTION 3 — MID-AIR SHIELD ====================== */}
      <section className="relative border-b border-brass/40 bg-navy text-parchment overflow-hidden" data-testid="shield-section">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute -top-32 left-1/4 w-[600px] h-[600px] rounded-full bg-brass/8 blur-3xl" />
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-oxblood/15 blur-3xl" />
          <img
            src="https://images.unsplash.com/photo-1589994965851-a8f479c573a9?w=1400&auto=format&fit=crop&q=60"
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover opacity-[0.07] mix-blend-luminosity"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          <div
            className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage:
                "linear-gradient(#B8894F 1px, transparent 1px), linear-gradient(90deg, #B8894F 1px, transparent 1px)",
              backgroundSize: "48px 48px",
            }}
          />
        </div>
        <div className="rule-brass-thick relative" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 text-brass-400" data-testid="shield-eyebrow">
              <div className="w-10 rule-brass" />
              <span className="text-[10px] font-bold tracking-[0.32em] uppercase font-mono">
                § III · The Mid-Air Shield
              </span>
            </div>
            <h2 className="font-legal text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] mt-6 text-parchment">
              An active runtime engine.
              <br />
              <span className="font-italic-legal italic text-brass-400 font-bold">
                Not a passive widget.
              </span>
            </h2>
            <div className="mt-5 rule-brass max-w-[60%]" />
            <p className="mt-6 text-base sm:text-lg text-parchment/85 leading-relaxed">
              Silent Shield intercepts and patches WCAG 2.1 &amp; 2.2 violations across Levels
              A / AA / AAA before the browser renders them &mdash; the exact remediation surface
              federal ADA Title III consent decrees have required.
            </p>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-4">
            {PILLARS.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.n}
                  className="relative bg-navy-900/60 backdrop-blur border-2 border-brass/40 p-7 sm:p-8 flex flex-col group hover:border-brass hover:bg-navy-900/85 transition-all duration-200"
                  data-testid={`shield-pillar-${p.n}`}
                >
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-brass to-transparent opacity-70 group-hover:opacity-100 transition-opacity" aria-hidden="true" />
                  <div className="flex items-start justify-between">
                    <div className="w-12 h-12 border-2 border-brass/70 group-hover:border-brass bg-brass/10 grid place-items-center">
                      <Icon className="w-5 h-5 text-brass-400" strokeWidth={2} />
                    </div>
                    <div className="font-legal text-4xl font-black text-brass/25 group-hover:text-brass/45 leading-none italic">
                      {p.n}
                    </div>
                  </div>
                  <div className="font-legal text-2xl font-black tracking-tight mt-6 text-parchment leading-[1.1]">
                    {p.title}
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-parchment/75 group-hover:text-parchment/95 flex-1">
                    {p.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ====================== SECTION 4 — REVENUE CONTRAST ====================== */}
      <section className="border-b border-brass/30 bg-parchment-50" data-testid="cost-contrast-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 text-brass-600" data-testid="cost-eyebrow">
              <div className="w-10 rule-brass" />
              <span className="text-[10px] font-bold tracking-[0.32em] uppercase font-mono">
                § IV · The Cost Contrast
              </span>
            </div>
            <h2 className="font-legal text-4xl sm:text-5xl font-black tracking-tight leading-[1.05] mt-6 text-navy">
              Two ledgers.{" "}
              <span className="font-italic-legal italic text-oxblood">Pick the one</span> you can survive.
            </h2>
            <div className="mt-5 rule-brass max-w-[50%]" />
          </div>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-px bg-brass/40">
            {/* COLUMN A — TRUE COST */}
            <div className="bg-parchment border-t-[6px] border-oxblood p-6 sm:p-10" data-testid="cost-column-litigation">
              <div className="flex items-center gap-2 text-[10px] font-bold tracking-[0.32em] text-oxblood uppercase">
                <AlertOctagon className="w-3.5 h-3.5" /> § Without Silent Shield
              </div>
              <h3 className="mt-3 font-legal text-2xl sm:text-3xl font-black tracking-tight text-navy">
                The true cost of a demand letter.
              </h3>
              <ul className="mt-6 space-y-4">
                {[
                  { stat: "$5,000–$25,000+", label: "Immediate settlement demand. Cash, due in 30 days." },
                  { stat: "$8,000–$40,000", label: "Billable defense attorney fees during negotiation." },
                  { stat: "60–90 days", label: "Forced source-code overhaul under strict court deadline." },
                  { stat: "Public record", label: "Filed lawsuit appears on PACER. Insurers raise premiums." },
                ].map((row) => (
                  <li key={row.stat} className="border-l-4 border-oxblood pl-4 py-1">
                    <div className="font-legal text-3xl font-black text-oxblood tabular-nums">
                      {row.stat}
                    </div>
                    <div className="text-sm text-ink-800 leading-snug mt-1">{row.label}</div>
                  </li>
                ))}
              </ul>
              <div className="mt-7 pt-5 border-t border-brass/30 text-[11px] text-ink-800/70 leading-relaxed font-mono">
                Conservative range. Federal ADA Title III settlements + defense costs
                routinely exceed $50K total. Source: legal-defense industry averages 2024.
              </div>
            </div>

            {/* COLUMN B — SILENT SHIELD */}
            <div className="bg-navy text-parchment p-6 sm:p-10 border-t-[6px] border-brass" data-testid="cost-column-shield">
              <div className="flex items-center gap-2 text-[10px] font-bold tracking-[0.32em] text-brass-400 uppercase">
                <CheckCircle2 className="w-3.5 h-3.5" /> § With Silent Shield
              </div>
              <h3 className="mt-3 font-legal text-2xl sm:text-3xl font-black tracking-tight text-parchment">
                The Silent Shield Protocol.
              </h3>
              <p className="mt-3 text-parchment/80 text-sm leading-relaxed">
                Continuous background harvesting. Instant runtime protection.
                Total infrastructure control.
              </p>
              <div className="mt-7 grid grid-cols-3 gap-2 sm:gap-3" data-testid="pricing-tiers">
                {TIERS.map((t) => {
                  const isSelected = selectedTier === t.packageId;
                  return (
                    <button
                      key={t.name}
                      type="button"
                      onClick={() => setSelectedTier(t.packageId)}
                      aria-pressed={isSelected}
                      className={`relative p-3 sm:p-4 text-left border-2 transition-all cursor-pointer ${
                        isSelected
                          ? "border-brass bg-brass/15 ring-2 ring-brass ring-offset-2 ring-offset-navy"
                          : "border-brass/40 hover:border-brass/70"
                      }`}
                      data-testid={t.testid}
                    >
                      {t.highlight && (
                        <div className="absolute -top-3 left-2 bg-brass text-navy text-[9px] font-bold tracking-wider uppercase px-1.5 py-0.5">
                          Most Picked
                        </div>
                      )}
                      <div className="text-[10px] font-bold tracking-[0.28em] text-brass-400 uppercase">
                        {t.name}
                      </div>
                      <div className="mt-1 flex items-baseline gap-0.5">
                        <span className="font-mono text-xs text-parchment/60">$</span>
                        <span className="font-legal text-3xl sm:text-4xl font-black tabular-nums text-parchment">
                          {t.price}
                        </span>
                      </div>
                      <div className="text-[10px] text-parchment/60 font-mono">{t.period}</div>
                      {t.note && (
                        <div className="mt-1 text-[10px] font-bold text-brass-400">{t.note}</div>
                      )}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={startCheckout}
                disabled={checkoutBusy}
                className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-brass hover:bg-brass-400 disabled:opacity-60 text-navy font-bold uppercase tracking-[0.18em] text-sm py-4 transition-colors border-2 border-brass"
                data-testid="pricing-tier-cta"
              >
                {checkoutBusy ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting to checkout…</>
                ) : (
                  <>Get Protected — Pay Securely <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
              <div className="mt-3 text-[11px] text-parchment/60 leading-relaxed font-mono text-center">
                Prefer to chat first?{" "}
                <Link to="/schedule" className="underline hover:text-brass-400">Schedule a 10 min call</Link>{" "}
                — no card required.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====================== SECTION 5 — CLOSING CAPTURE ====================== */}
      <section className="border-b border-brass/30 bg-parchment-200/40" data-testid="closing-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <div className="flex items-center justify-center gap-3 text-brass-600" data-testid="closing-eyebrow">
              <div className="w-10 rule-brass" />
              <span className="text-[10px] font-bold tracking-[0.32em] uppercase font-mono">
                § V · Risk Reversal
              </span>
              <div className="w-10 rule-brass" />
            </div>
          </div>
          <FooterUrlCapture />
        </div>
      </section>

      {/* ====================== FOOTER ====================== */}
      </main>
      <footer className="bg-parchment-50" data-testid="marketing-footer">
        <div className="rule-brass-thick" aria-hidden="true" />
        {/* Trust-seal strip — links to /compliance documentation */}
        <div className="border-b border-brass/30 bg-parchment">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
            <Link
              to="/compliance"
              className="group inline-flex items-center gap-3 hover:bg-parchment-50 px-3 py-2 -mx-3 -my-2 transition-colors"
              data-testid="footer-trust-badge"
              aria-label="Silent Shield Verified compliance report"
            >
              <div className="w-12 h-12 bg-zinc-950 text-white grid place-items-center border-2 border-zinc-950 relative shrink-0">
                <CheckCircle2 className="w-6 h-6 text-blue-400" strokeWidth={2} />
                <div className="absolute -bottom-1.5 -right-1.5 bg-blue-700 text-white text-[8px] font-bold tracking-wider px-1 py-0.5">
                  AA
                </div>
              </div>
              <div>
                <div className="text-[10px] font-bold tracking-[0.28em] text-blue-700 uppercase">
                  Silent Shield Verified
                </div>
                <div className="font-display font-bold tracking-tight text-sm group-hover:text-blue-700 transition-colors">
                  This site passes WCAG 2.1 AA
                  <ChevronRight className="w-3 h-3 inline ml-0.5 -mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
            </Link>
            <p className="sm:ml-auto text-[11px] text-zinc-500 font-mono leading-snug max-w-sm sm:text-right">
              Audited by the same engine we deploy to protect our customers.
              <Link to="/compliance" className="ml-1 underline hover:text-blue-700">
                View report →
              </Link>
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div>
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 border-2 border-zinc-950 grid place-items-center">
                <ShieldAlert className="w-3.5 h-3.5" strokeWidth={2.5} />
              </div>
              <div className="font-display font-black tracking-tight text-base">SILENT SHIELD</div>
            </div>
            <p className="mt-3 text-xs text-zinc-500 leading-relaxed max-w-xs">
              An ADA Interceptor product. Built for businesses that want real protection
              instead of theatrical compliance.
            </p>
          </div>
          <div>
            <div className="text-[10px] font-bold tracking-[0.28em] text-zinc-500 uppercase">
              Contact
            </div>
            <div className="mt-2 space-y-1">
              <a href="mailto:support@adainterceptor.com" className="block text-xs text-zinc-700 hover:text-blue-700 font-mono">
                support@adainterceptor.com
              </a>
              <Link to="/schedule" className="block text-xs text-zinc-700 hover:text-blue-700 font-bold uppercase tracking-wider mt-2">
                Schedule a Call <ChevronRight className="w-3 h-3 inline" />
              </Link>
            </div>
          </div>
          <div className="sm:text-right">
            <div className="text-[10px] font-bold tracking-[0.28em] text-zinc-500 uppercase">
              Compliance
            </div>
            <Link
              to="/compliance"
              className="inline-flex items-center gap-1.5 mt-2 text-xs font-bold uppercase tracking-wider text-zinc-700 hover:text-blue-700"
              data-testid="footer-compliance-link"
            >
              <Code2 className="w-3 h-3" /> Trust &amp; Accessibility
            </Link>
            <div className="mt-4 text-[10px] text-zinc-400 font-mono">
              © {new Date().getFullYear()} ADA Interceptor. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
